import { ClientLocaleEntity } from '@edgebox/sync-core-rest-client';

/**
 * How a locale reads to an operator: "English (United States)", never `en_US` and never an id.
 *
 * One definition rather than one per screen, because a locale named two ways on two screens is the
 * same defect as a term styled two ways — the reader cannot tell whether they are looking at the
 * same thing. The term form, the term list's create step and a term's own page all render locales
 * through here.
 */
export function localeDisplayName(locale: ClientLocaleEntity) {
  const languageName = locale.language?.englishName || locale.language?.name || locale.language?.code;
  const countryName = locale.country?.englishName || locale.country?.name || locale.country?.code;

  if (languageName && countryName) {
    return `${languageName} (${countryName})`;
  }

  return languageName || locale.isoCode || locale.code;
}

/**
 * The id inside a locale reference, whether it arrived resolved or as a bare reference.
 *
 * A reference is not the locale: it carries an id, and the name the reader needs lives on the
 * loaded locale that id points at. This returns the id so a caller can look that locale up — it is
 * never something to put on screen.
 */
export function localeReferenceId(reference: unknown): string | undefined {
  if (!reference || typeof reference !== 'object') {
    return undefined;
  }

  const candidate = reference as { getId?: () => unknown; id?: unknown };
  const fromGetter = candidate.getId?.();
  if (fromGetter) {
    return String(fromGetter);
  }

  if (candidate.id) {
    return String(candidate.id);
  }

  return undefined;
}
